import gsap from 'gsap'

export default class InteractiveGoo {
	constructor() {
		this.instances = Array.from(document.querySelectorAll('[data-goo]'))
			.map(svg => this.#createInstance(svg))
			.filter(Boolean)
	}

	#createInstance(svg) {
		const blobs = Array.from(svg.querySelectorAll('[data-goo-blob]'))
		if (!blobs.length) return null

		const viewBox = svg.viewBox?.baseVal
		const VIEW_W = viewBox?.width || svg.clientWidth || 1
		const VIEW_H = viewBox?.height || svg.clientHeight || 1

		blobs.forEach(blob => gsap.set(blob, { x: VIEW_W / 2, y: VIEW_H / 2, scale: 0 }))

		const motions = blobs.map((blob, i) => ({
			blob,
			i,
			xTo: gsap.quickTo(blob, 'x', { duration: 0.35 + i * 0.08, ease: 'power3.out' }),
			yTo: gsap.quickTo(blob, 'y', { duration: 0.35 + i * 0.08, ease: 'power3.out' }),
		}))

		const state = { active: false, velocity: 0, lastTargetScale: 1 }

		const VELOCITY_GAIN = 0.25
		const VELOCITY_CAP = 0.5
		const VELOCITY_DECAY = 0.9

		let lastClientX = null
		let lastClientY = null
		let lastT = 0

		const toCoords = (clientX, clientY) => {
			const rect = svg.getBoundingClientRect()
			if (!rect.width || !rect.height) return null
			const rawX = ((clientX - rect.left) / rect.width) * VIEW_W
			const rawY = ((clientY - rect.top) / rect.height) * VIEW_H
			return {
				x: Math.max(0, Math.min(VIEW_W, rawX)),
				y: Math.max(0, Math.min(VIEW_H, rawY)),
			}
		}

		const applyActiveScale = () => {
			const target = 1 + Math.min(state.velocity * VELOCITY_GAIN, VELOCITY_CAP)
			if (Math.abs(target - state.lastTargetScale) < 0.01) return
			state.lastTargetScale = target
			motions.forEach(({ blob, i }) => {
				gsap.to(blob, {
					scale: target,
					duration: 0.3 + i * 0.04,
					ease: 'power2.out',
					overwrite: 'auto',
				})
			})
		}

		const onMove = e => {
			const c = toCoords(e.clientX, e.clientY)
			if (!c) return

			const now = performance.now()
			if (lastClientX !== null) {
				const dt = Math.max(now - lastT, 1)
				const dx = e.clientX - lastClientX
				const dy = e.clientY - lastClientY
				const instantV = Math.sqrt(dx * dx + dy * dy) / dt
				state.velocity = state.velocity * 0.6 + instantV * 0.4
			}
			lastClientX = e.clientX
			lastClientY = e.clientY
			lastT = now

			if (!state.active) {
				blobs.forEach(blob => gsap.set(blob, { x: c.x, y: c.y }))
				state.active = true
				state.lastTargetScale = 0
			}

			motions.forEach(({ xTo, yTo }) => {
				xTo(c.x)
				yTo(c.y)
			})

			applyActiveScale()
		}

		const onLeave = () => {
			state.active = false
			state.velocity = 0
			state.lastTargetScale = 0
			motions.forEach(({ blob, i }) => {
				gsap.to(blob, {
					scale: 0,
					duration: 0.45 + i * 0.05,
					ease: 'power2.in',
					overwrite: 'auto',
				})
			})
		}

		const tick = () => {
			if (!state.active) return
			state.velocity *= VELOCITY_DECAY
			applyActiveScale()
		}

		svg.addEventListener('mousemove', onMove)
		svg.addEventListener('mouseleave', onLeave)
		gsap.ticker.add(tick)

		return {
			destroy() {
				svg.removeEventListener('mousemove', onMove)
				svg.removeEventListener('mouseleave', onLeave)
				gsap.ticker.remove(tick)
			},
		}
	}

	destroy() {
		this.instances.forEach(i => i.destroy())
		this.instances = []
	}
}
